/**
 * The three art-direction presets a house can choose between.
 *
 * They change TYPE and SHAPE only — heading font, label treatment, corner
 * radius, section rhythm. Never colour, never the position of a box. A tenant's
 * choice therefore cannot break a page, and cannot touch the locked auction
 * colour semantics (amber live, red closing/outbid, green winning).
 *
 * Shared because the value is written by the admin, stored in
 * `tenants.metadata.branding.preset`, published to anonymous callers through
 * `get_house_identity`, and read by the bidder shell — four places that must
 * agree on the spelling and on what happens to a value none of them recognise.
 */

export const BIDDER_PRESETS = ['modern', 'classic', 'editorial'] as const;

export type BidderPreset = (typeof BIDDER_PRESETS)[number];

/** What a house gets when it has never chosen. */
export const DEFAULT_BIDDER_PRESET: BidderPreset = 'modern';

/** Short labels for a picker. Kept beside the list so one cannot outlive the other. */
export const BIDDER_PRESET_LABELS: Readonly<Record<BidderPreset, string>> = {
  modern: 'Modern',
  classic: 'Classic',
  editorial: 'Editorial',
};

export const BIDDER_PRESET_BLURBS: Readonly<Record<BidderPreset, string>> = {
  modern: 'Clean sans-serif, rounded corners. The default, and the safest choice.',
  classic: 'Softer corners with italic serif labels — a traditional, heritage feel.',
  editorial: 'Serif headlines and square corners. Photography-first, for art and high-end lots.',
};

/**
 * 🔴 AN UNRECOGNISED PRESET FALLS BACK, IT NEVER PASSES THROUGH.
 *
 * `data-preset` drives a `[data-preset='…']` CSS selector. A value with no
 * matching stylesheet matches nothing, so the page renders with none of the
 * preset's type or shape tokens — an unstyled page rather than a visible error,
 * which reads as a bug in the design rather than a bad value in a database.
 *
 * The write path validates too (`set_branding`). This is the second gate,
 * because the value arrives from a JSON bag that a future migration, a manual
 * fix or a restored backup could put anything into.
 */
export function resolveBidderPreset(value: unknown): BidderPreset {
  return typeof value === 'string' && (BIDDER_PRESETS as readonly string[]).includes(value)
    ? (value as BidderPreset)
    : DEFAULT_BIDDER_PRESET;
}
